import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Target, Award, Leaf, HandHeart, Heart, ArrowRight, CheckCircle2 } from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';
import PublicLayout, { PageHero } from '../components/public/PublicLayout';
import { EASE, Reveal, Stagger, StaggerItem, SectionHeading, Eyebrow, KineticText } from '../components/public/motion';

const VALUES = [
  'Long-term relationships over volume recruiting',
  'Transparent pricing on every engagement',
  'Candidates who deliver measurable outcomes from day one',
  'Hands-on directors across every account',
];

const SOCIAL_ITEMS = [
  {
    icon: Leaf,
    title: 'Environmental Impact',
    desc: 'We offset the carbon footprint of every employee through certified reforestation initiatives.',
  },
  {
    icon: HandHeart,
    title: 'Giving Back',
    desc: 'In consultation with our employees, we donate quarterly to charities that reflect our shared values.',
  },
  {
    icon: Heart,
    title: 'Employee Support',
    desc: 'We offer counselling and financial wellbeing services to support the personal and professional lives of our team.',
  },
];

const WhoWeAre = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['-8%', '8%']);

  return (
    <section ref={ref} className="overflow-hidden bg-white py-20 md:py-28">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 md:px-8 lg:grid-cols-2 lg:gap-20">
        <Reveal className="relative order-2 lg:order-1">
          <div className="relative overflow-hidden rounded-3xl shadow-2xl">
            <motion.img
              src="https://images.unsplash.com/photo-1594732832278-abd644401426?q=80&w=2070&auto=format&fit=crop&w=1000&q=80"
              alt="The Appsytec team"
              className="h-[26rem] w-full scale-110 object-cover md:h-[32rem]"
              style={{ y: imgY }}
            />
          </div>
          <div aria-hidden="true" className="absolute -left-4 -top-4 h-24 w-24 rounded-tl-3xl border-l-4 border-t-4 border-red-500" />
          <div aria-hidden="true" className="absolute -bottom-4 -right-4 h-24 w-24 rounded-br-3xl border-b-4 border-r-4 border-red-500" />
        </Reveal>

        <div className="order-1 lg:order-2">
          <Reveal y={16}>
            <Eyebrow>Who we are</Eyebrow>
          </Reveal>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-gray-900 md:text-5xl">
            <KineticText text="Boutique by design, not by accident" />
          </h2>
          <Reveal delay={0.15}>
            <p className="mt-6 text-lg text-gray-600 md:text-xl">
              Appsytec is a boutique Australian SME focused on making procurement effortless. We specialise in
              connecting public sector organisations with high-calibre ICT professionals.
            </p>
            <p className="mt-4 text-gray-600">
              With hands-on directors and a proven track record across Commonwealth and state departments and
              private enterprise, we don't just fill roles; we help clients build capability.
            </p>
          </Reveal>
          <Stagger className="mt-8 space-y-3" gap={0.08} delay={0.2}>
            {VALUES.map((value) => (
              <StaggerItem key={value} y={14} className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
                <span className="text-gray-700">{value}</span>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </div>
    </section>
  );
};

const MissionVision = () => (
  <section className="relative overflow-hidden bg-gray-950 py-20 text-white md:py-28">
    <div className="pointer-events-none absolute inset-0">
      <div className="absolute -left-24 top-1/4 h-80 w-80 rounded-full bg-red-500/15 blur-3xl" />
      <div
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
          backgroundSize: '56px 56px',
        }}
      />
    </div>

    <div className="relative mx-auto max-w-7xl px-4 md:px-8">
      <SectionHeading dark eyebrow="Mission & vision" title="Our driving purpose" />

      <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
        {[
          {
            icon: Target,
            title: 'Our Mission',
            desc: 'To connect organisations with highly vetted ICT specialists who deliver measurable outcomes from day one, through personalised service, ethical practices, and a focus on long-term success.',
          },
          {
            icon: Award,
            title: 'Our Vision',
            desc: 'To be the preferred values-driven partner for ICT talent across Australia, recognised for quality, transparency, and positive impact in every engagement.',
          },
        ].map((item, i) => {
          const Icon = item.icon;
          return (
            <Reveal key={item.title} delay={0.1 + i * 0.15}>
              <motion.div
                whileHover={{ y: -6 }}
                transition={{ duration: 0.3, ease: EASE }}
                className="group h-full rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur-sm transition-colors duration-300 hover:border-red-500/40 md:p-10"
              >
                <div className="inline-flex rounded-2xl bg-red-500 p-3.5 transition-transform duration-300 group-hover:scale-110">
                  <Icon className="h-7 w-7" />
                </div>
                <h3 className="mt-6 font-display text-2xl font-bold">{item.title}</h3>
                <p className="mt-4 text-lg leading-relaxed text-gray-400">{item.desc}</p>
              </motion.div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const SocialResponsibility = () => (
  <section className="bg-gray-50 py-20 md:py-28">
    <div className="mx-auto max-w-7xl px-4 md:px-8">
      <SectionHeading
        eyebrow="Social responsibility"
        title="Impact beyond placements"
        subtitle="Making a positive difference in our communities and environment."
      />

      <Stagger className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3" gap={0.12}>
        {SOCIAL_ITEMS.map((item) => {
          const Icon = item.icon;
          return (
            <StaggerItem key={item.title}>
              <motion.div
                whileHover={{ y: -6 }}
                transition={{ duration: 0.3, ease: EASE }}
                className="group h-full rounded-3xl border border-gray-200 bg-white p-8 text-center transition-shadow duration-300 hover:shadow-xl hover:shadow-gray-900/5"
              >
                <div className="mx-auto inline-flex rounded-2xl bg-red-50 p-4 text-red-500 transition-colors duration-300 group-hover:bg-red-500 group-hover:text-white">
                  <Icon className="h-7 w-7" />
                </div>
                <h3 className="mt-6 font-display text-xl font-bold text-gray-900">{item.title}</h3>
                <p className="mt-3 text-gray-600">{item.desc}</p>
              </motion.div>
            </StaggerItem>
          );
        })}
      </Stagger>

      <Reveal delay={0.2} className="mt-16 text-center">
        <Link
          to="/contact"
          className="group inline-flex items-center gap-2 rounded-full bg-gray-900 px-8 py-4 font-semibold text-white transition-all duration-300 hover:bg-red-500"
        >
          Work with us
          <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const AboutPage = () => (
  <PublicLayout>
    <PageHero
      eyebrow="About us"
      title="People-first ICT recruitment"
      subtitle="Our strength lies in being small, agile, and accountable: building long-term relationships across Commonwealth, state and private sectors."
    />
    <WhoWeAre />
    <MissionVision />
    <SocialResponsibility />
  </PublicLayout>
);

export default AboutPage;
